/** producer-notifications (Slice 1) — the shell's notification feed. Delivery is
    by polling; the hooks refetch on an interval so a new message or engagement
    change shows up without the user reloading. Real-time transport (SSE) and push
    are deferred. */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";

import { useAuth } from "../auth";
import { apiFetch } from "../http";

export type NotificationKind = "message" | "engagement";

export interface Notification {
  id: string;
  kind: NotificationKind;
  title: string;
  body: string;
  job_id: string | null;
  conversation_id: string | null;
  created_at: string;
  read_at: string | null;
}

/** How often the feed and the unread badge refetch (ms). */
const POLL_MS = 45_000;

const keys = {
  list: ["pencil", "notifications"] as const,
  unread: ["pencil", "notifications", "unread"] as const,
};

function useToken() {
  const { getAccessToken } = useAuth();
  return useCallback(() => getAccessToken(), [getAccessToken]);
}

export function useNotifications(): UseQueryResult<Notification[], Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.list,
    refetchInterval: POLL_MS,
    queryFn: async ({ signal }) => {
      const res = await apiFetch<{ items: Notification[] }>("/pencil/notifications", {
        token: await token(),
        signal,
      });
      return res.items;
    },
  });
}

export function useUnreadCount(): UseQueryResult<number, Error> {
  const token = useToken();
  return useQuery({
    queryKey: keys.unread,
    refetchInterval: POLL_MS,
    queryFn: async ({ signal }) => {
      const res = await apiFetch<{ count: number }>("/pencil/notifications/unread-count", {
        token: await token(),
        signal,
      });
      return res.count;
    },
  });
}

/** Mark notifications read — a given set, or all when `ids` is omitted. */
export function useMarkNotificationsRead(): UseMutationResult<
  number,
  Error,
  { ids?: string[] } | void
> {
  const token = useToken();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const res = await apiFetch<{ marked: number }>("/pencil/notifications/read", {
        method: "POST",
        token: await token(),
        body: { ids: (arg && "ids" in arg ? arg.ids : undefined) ?? null },
      });
      return res.marked;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.list });
      void qc.invalidateQueries({ queryKey: keys.unread });
    },
  });
}
